/**
 * DATA NORMALIZER
 * Normalisiert Daten für konsistente Berechnungen
 * 
 * Löst das Problem unterschiedlicher Feldnamen:
 * - sale.total vs sale.totalAmount
 * - purchase.date vs purchase.purchaseDate
 * - etc.
 */

import { getSaleTotalAmount } from '../domain/restaurant/restaurantDishSaleService';
import { isPurchaseVoided } from '../domain/purchases/purchaseVoidStatus';

/**
 * Normalisiert Sale-Objekt
 */
export const normalizeSale = (sale) => {
  if (!sale || typeof sale !== 'object') {
    return {
      id: null,
      total: 0,
      date: new Date().toISOString(),
      products: []
    };
  }
  
  return {
    ...sale,
    id: sale.id || sale.saleId || sale._id || null,
    // Void-/cancel-aware SSOT — never prefer raw stored total over active amount.
    total: getSaleTotalAmount(sale),
    date: sale.date || sale.saleDate || sale.createdAt || sale.timestamp || new Date().toISOString(),
    products: Array.isArray(sale.products) ? sale.products : []
  };
};

/**
 * Normalisiert Purchase-Objekt
 */
export const normalizePurchase = (purchase) => {
  if (!purchase || typeof purchase !== 'object') {
    return {
      id: null,
      total: 0,
      date: new Date().toISOString(),
      products: []
    };
  }

  // Soft-voided achats contribute 0 to spend / profit fallback totals (ADR-0016)
  const voided = isPurchaseVoided(purchase);
  const liveTotal =
    Number(purchase.total) || Number(purchase.totalAmount) || Number(purchase.amount) || 0;

  return {
    ...purchase,
    id: purchase.id || purchase.purchaseId || purchase._id || null,
    total: voided ? 0 : liveTotal,
    date: purchase.date || purchase.purchaseDate || purchase.createdAt || purchase.timestamp || new Date().toISOString(),
    products: Array.isArray(purchase.products) ? purchase.products : [],
    productId: purchase.productId || purchase.product?.id || null
  };
};

/**
 * Normalisiert Expense-Objekt
 */
export const normalizeExpense = (expense) => {
  if (!expense || typeof expense !== 'object') {
    return {
      id: null,
      amount: 0,
      date: new Date().toISOString()
    };
  }
  
  return {
    ...expense,
    id: expense.id || expense.expenseId || expense._id || null,
    amount: Number(expense.amount) || Number(expense.value) || 0,
    date: expense.date || expense.expenseDate || expense.createdAt || expense.timestamp || new Date().toISOString()
  };
};

/**
 * Normalisiert Lots (Array oder Object)
 */
export const normalizeLots = (lots) => {
  if (Array.isArray(lots)) return lots;
  if (lots && typeof lots === 'object') return Object.values(lots);
  return [];
};

/**
 * Normalisiert Product-Objekt
 */
export const normalizeProduct = (product) => {
  if (!product || typeof product !== 'object') {
    return {
      id: null,
      name: '',
      category: 'Uncategorized',
      location: 'Principal',
      quantity: 0
    };
  }
  
  return {
    ...product,
    id: product.id || product.productId || product._id || null,
    name: product.name || product.productName || '',
    category: product.category || 'Uncategorized',
    subcategory: product.subcategory || '',
    type: product.type || '',
    location: product.location || 'Principal',
    quantity: Number(product.quantity) || Number(product.stock) || 0,
    purchaseUnitPrice: Number(product.purchaseUnitPrice) || Number(product.purchasePrice) || 0,
    sellingUnitPrice: Number(product.sellingUnitPrice) || Number(product.sellingPrice) || Number(product.price) || 0
  };
};

/**
 * Normalisiert alle Daten auf einmal
 */
export const normalizeAllData = (data) => {
  return {
    products: (data.products || []).map(normalizeProduct),
    lots: normalizeLots(data.lots),
    sales: (data.sales || []).map(normalizeSale),
    purchases: (data.purchases || []).map(normalizePurchase),
    expenses: (data.expenses || []).map(normalizeExpense)
  };
};

